import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { FC, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@/components/ui/pagination";
import { PaginationInfo } from "@/data/types";
import { cn } from "@/lib/utils";

type Props = {
  paginationInfo?: PaginationInfo;
};

export const TablePagination: FC<Props> = ({ paginationInfo }) => {
  const [searchParams] = useSearchParams();

  const pagination = useMemo(
    () => ({
      pageIndex: Number(searchParams.get("page")) || 1,
      pageSize: Number(searchParams.get("limit")) || 20,
    }),
    [searchParams],
  );

  const generateLink = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams);

      params.set(key, value);

      return `?${params.toString()}`;
    },
    [searchParams],
  );

  return (
    <div className="flex-1 flex justify-center">
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <Link
              to={generateLink("page", (pagination.pageIndex - 1).toString())}
              preventScrollReset
              className={cn(
                pagination.pageIndex === 1 && "pointer-events-none",
              )}
            >
              <Button variant="ghost" disabled={pagination.pageIndex === 1}>
                <ChevronLeftIcon />
                <span className="hidden sm:block">Previous</span>
              </Button>
            </Link>
          </PaginationItem>
          {paginationInfo?.series.map((series, index) => {
            if (typeof series === "number") {
              return (
                <PaginationItem key={index}>
                  <Link
                    to={generateLink("page", series.toString())}
                    preventScrollReset
                  >
                    <Button variant="ghost" size="icon">
                      {series}
                    </Button>
                  </Link>
                </PaginationItem>
              );
            } else if (series === "gap") {
              return <PaginationEllipsis key={index} />;
            } else {
              return (
                <PaginationItem key={index}>
                  <Link
                    to={generateLink("page", series.toString())}
                    preventScrollReset
                  >
                    <Button variant="outline" size="icon">
                      {series}
                    </Button>
                  </Link>
                </PaginationItem>
              );
            }
          })}
          <PaginationItem>
            <Link
              to={generateLink("page", (pagination.pageIndex + 1).toString())}
              className={cn(
                pagination.pageIndex === paginationInfo?.pages &&
                  "pointer-events-none",
              )}
              preventScrollReset
            >
              <Button
                variant="ghost"
                disabled={pagination.pageIndex === paginationInfo?.pages}
              >
                <span className="hidden sm:block">Next</span>
                <ChevronRightIcon />
              </Button>
            </Link>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
